import React, { useContext } from "react";
import * as Styled from "./Styled";
import type { Investment } from "@interfaces/investment";
import {
  formatDate,
  numberToCurrency,
  rangeOfNumbers,
} from "@components/utils/formatters";
import SvgInfo from "@components/ui/icons/info";
import InfoHover from "@components/ui/info-hover";
import { DisplayAmount } from "@components/display-amount-context";

type Props = {
  investment: Investment;
  shareName: string;
  availableRewardsPercentage: number;
};

export const InvestmentCardColumns: React.FC<Props> = ({
  investment,
  shareName,
  availableRewardsPercentage,
}) => {
  const {
    state: { displayAmount },
  } = useContext(DisplayAmount);

  const showValue = (value: any) => {
    return displayAmount ? value : "••••";
  };

  const {
    shares_acquired,
    amount_value,
    status,
    amount_redempted,
    rewards_sum,
    amount_remaining,
    amount_remaining_description,
    duration,
    opportunity,
    paid_at,
  } = investment;

  const { estimated_profitability, precatorios, estimated_deadline } =
    opportunity;

  const items = [
    paid_at
      ? {
          label: "Data do investimento",
          value: formatDate(paid_at),
        }
      : {
          label: `Número de ${shareName}s`,
          value: shares_acquired,
        },
    {
      label: "Valor investido",
      value: showValue(numberToCurrency(amount_value)),
    },
    ...(["partially_received", "redempted"].includes(status)
      ? [
          {
            label: "Valor recebido",
            value: showValue(numberToCurrency(amount_redempted)),
          },
          {
            label: "Bônus",
            value: showValue(numberToCurrency(rewards_sum)),
          },
          status === "partially_received"
            ? {
                label: "Valor remanescente",
                value: (
                  <Styled.InfoData>
                    {showValue(numberToCurrency(amount_remaining))}
                    <InfoHover
                      icon={<SvgInfo color="#1D297C" width="16" />}
                      content={amount_remaining_description}
                    />
                  </Styled.InfoData>
                ),
              }
            : {
                label: "Duração",
                value: `${Math.floor(duration / 30)} meses`,
              },
        ]
      : [
          {
            label: "Retorno projetado",
            value: estimated_profitability,
          },
          precatorios
            ? {
                label: "Vencimento dos precatórios",
                value: `${rangeOfNumbers(
                  precatorios.map((item) => item.estimated_deadline)
                )} meses`,
              }
            : {
                label: "Prazo estimado",
                value: estimated_deadline,
              },
          status === "paid"
            ? {
                label: "Bônus",
                value: showValue(numberToCurrency(rewards_sum)),
              }
            : {
                label: "Bônus a receber",
                value: showValue(
                  numberToCurrency(
                    (amount_value * Math.min(availableRewardsPercentage, 1.5)) /
                      100
                  )
                ),
              },
        ]),
  ];

  return (
    <Styled.ColumnsGrid>
      {items.map(({ label, value }, i) => (
        <Styled.ColumnsItem key={i}>
          {label}
          <span>{value}</span>
        </Styled.ColumnsItem>
      ))}
    </Styled.ColumnsGrid>
  );
};
